import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import QrCodeDialog from "../components/QrCodeDialog";
import RsvpListDialog from "../components/RsvpListDialog";
import PhoneVerifyDialog from "../components/PhoneVerifyDialog";
import WeatherStrip from "../components/WeatherStrip";
import MapButton from "../components/MapButton";
import { toast } from "sonner";
import {
  MapPin,
  Clock,
  ArrowLeft,
  Share2,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  QrCode,
  PlayCircle,
  Rocket,
  Users,
  Flag,
  Copy,
} from "lucide-react";
import {
  formatDatePt,
  formatTime,
  isPast as checkPast,
  buildWhatsAppShareText,
  maskPhoneBR,
} from "../lib/format";

export default function RideDetailPage() {
  const { id } = useParams();
  const [ride, setRide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [rsvpStats, setRsvpStats] = useState(null);
  const [form, setForm] = useState({
    rider_name: "",
    phone: "",
    companion: "",
    motorcycle_model: "",
    is_guest: false,
  });
  const [lookingUp, setLookingUp] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);

  const lookupByPhone = async () => {
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length !== 11) return;
    setLookingUp(true);
    try {
      const res = await api.post("/riders/lookup", { phone: form.phone });
      if (res.data?.found) {
        setForm((prev) => ({
          ...prev,
          rider_name: prev.rider_name || res.data.rider_name || "",
          motorcycle_model:
            prev.motorcycle_model || res.data.motorcycle_model || "",
        }));
        if (res.data.rider_name) {
          setAutoFilled(true);
          toast.success(`Bem-vindo de volta, ${res.data.rider_name.split(" ")[0]}!`);
        }
      }
    } catch {
      // silent fail — form still works manually
    } finally {
      setLookingUp(false);
    }
  };

  useEffect(() => {
    api
      .get(`/rides/${id}`)
      .then((res) => setRide(res.data))
      .catch(() => toast.error("Rolê não encontrado"))
      .finally(() => setLoading(false));
  }, [id]);

  // Fetch the public rider stats (no names — full list is admin-only).
  // Includes: count, confirmed_count, waitlist_count, max_riders.
  useEffect(() => {
    if (!ride) return;
    api
      .get(`/rides/${id}/rsvp-count`)
      .then((res) => setRsvpStats(res.data || null))
      .catch(() => setRsvpStats(null));
  }, [ride, id, success]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rider_name.trim() || !form.motorcycle_model.trim() || !form.phone.trim()) {
      toast.error("Preencha nome, telefone e modelo da moto");
      return;
    }
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (phoneDigits.length !== 11) {
      toast.error("Por favor, insira o número de telefone completo com DDD");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/rides/${id}/rsvp`, form);
      setSuccess({ ride, rsvp: res.data });
      toast.success("Presença confirmada!");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        toast.error(detail || "Este telefone já confirmou presença neste rolê.");
      } else {
        toast.error(detail || "Erro ao confirmar. Tente novamente.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 label-caps">
        Carregando...
      </div>
    );
  }

  if (!ride) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16">
        <div className="label-caps text-white/60">Rolê não encontrado.</div>
        <Link to="/" className="btn-primary inline-block px-5 py-2 mt-4 text-sm">
          Voltar
        </Link>
      </div>
    );
  }

  const past = checkPast(ride.ride_datetime);
  const shareText = buildWhatsAppShareText(ride, window.location.href);
  const confirmedCount = rsvpStats?.confirmed_count ?? rsvpStats?.count ?? 0;
  const waitlistCount = rsvpStats?.waitlist_count ?? 0;
  const maxRiders = rsvpStats?.max_riders ?? null;
  const rideIsFull = !!(maxRiders && confirmedCount >= maxRiders);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
        data-testid="back-to-home"
      >
        <ArrowLeft size={14} /> Voltar
      </Link>

      {/* Ride header */}
      <div className="mt-6 border border-white/10 bg-[#12141D]">
        <div className="p-6 sm:p-8 border-b border-white/10">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {past ? (
              <span className="label-caps px-2 py-1 border border-white/15 text-white/70">
                Passado
              </span>
            ) : (
              <span
                className="label-caps px-2 py-1 border"
                style={{
                  color: "#39FF14",
                  borderColor: "rgba(57,255,20,0.35)",
                }}
              >
                Próximo Rolê
              </span>
            )}
            {ride.is_official === false && (
              <span
                className="label-caps text-[10px] px-2 py-1 border"
                style={{ color: "#F59E0B", borderColor: "rgba(245,158,11,0.4)" }}
                data-testid="ride-unofficial-tag"
              >
                Encontro Livre
              </span>
            )}
            <span className="label-caps text-white/60">
              {formatDatePt(ride.ride_datetime)}
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl text-white uppercase tracking-tight">
            {ride.name}
          </h1>

          {past ? (
            <PastRideSummary
              ride={ride}
              rsvpCount={confirmedCount}
              onOpenList={() => setVerifyOpen(true)}
            />
          ) : (
            <>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <InfoBlock
                  icon={<MapButton address={ride.location} size={18} testid="ride-location-map" />}
                  label="Local"
                  value={ride.location}
                  link={ride.location_link}
                  testid="ride-location"
                />
                <InfoBlock
                  icon={<MapButton address={ride.meeting_point} size={18} testid="ride-meeting-map" />}
                  label="Ponto de Encontro"
                  value={ride.meeting_point}
                  link={ride.meeting_point_link}
                  testid="ride-meeting-point"
                />
                <InfoBlock
                  icon={<Clock size={16} style={{ color: "#39FF14" }} />}
                  label="Chegada"
                  value={ride.arrival_time}
                  testid="ride-arrival"
                />
                <InfoBlock
                  icon={<AlertTriangle size={16} style={{ color: "#F59E0B" }} />}
                  label="Saída — Sem atrasos!"
                  value={ride.departure_time}
                  testid="ride-departure"
                />
              </div>

              <WeatherStrip weather={ride.weather} />

              {/* Status block: capacity / waitlist / motivator */}
              <div className="mt-5" data-testid="rsvp-status-block">
                {rsvpStats && (
                  maxRiders ? (
                    <div className="space-y-3">
                      <div
                        className="border border-white/10 bg-gradient-to-r from-[#00E5FF]/8 to-[#39FF14]/8 px-5 py-4 flex items-center gap-3"
                        data-testid="rsvp-count-confirmed"
                      >
                        <div className="text-white font-body text-sm sm:text-base leading-snug flex-1">
                          🏍️{" "}
                          <span
                            className="font-display text-2xl text-[#39FF14] glow-green"
                            data-testid="rsvp-count-number"
                          >
                            {confirmedCount}
                          </span>
                          {" "}/{" "}
                          <span className="font-display text-2xl text-white">
                            {maxRiders}
                          </span>{" "}
                          Vagas Confirmadas
                        </div>
                        {rideIsFull && (
                          <span
                            className="label-caps text-[11px] px-2 py-1 border animate-pulse"
                            style={{
                              color: "#F59E0B",
                              borderColor: "rgba(245,158,11,0.55)",
                            }}
                            data-testid="ride-full-badge"
                          >
                            Lotado
                          </span>
                        )}
                      </div>
                      {waitlistCount > 0 && (
                        <div
                          className="border px-5 py-3 flex items-center gap-3"
                          style={{
                            borderColor: "rgba(245,158,11,0.45)",
                            background: "rgba(245,158,11,0.05)",
                          }}
                          data-testid="rsvp-waitlist-status"
                        >
                          <Users size={18} style={{ color: "#F59E0B" }} />
                          <div className="text-white font-body text-sm leading-snug">
                            <span
                              className="font-display text-[#F59E0B]"
                              data-testid="rsvp-waitlist-number"
                            >
                              {waitlistCount}
                            </span>{" "}
                            piloto{waitlistCount === 1 ? "" : "s"} na fila de espera
                          </div>
                        </div>
                      )}
                    </div>
                  ) : confirmedCount > 10 ? (
                    <div
                      className="border border-white/10 bg-gradient-to-r from-[#00E5FF]/8 to-[#39FF14]/8 px-5 py-4 flex items-center gap-3"
                      data-testid="rsvp-count-confirmed"
                    >
                      <div className="text-white font-body text-sm sm:text-base leading-snug">
                        🏍️{" "}
                        <span
                          className="font-display text-2xl text-[#39FF14] glow-green"
                          data-testid="rsvp-count-number"
                        >
                          {confirmedCount}
                        </span>{" "}
                        Pilotos Confirmados
                      </div>
                    </div>
                  ) : (
                    <div
                      className="border px-5 py-4 flex items-center gap-3"
                      style={{
                        borderColor: "rgba(0,229,255,0.25)",
                        background: "rgba(0,229,255,0.05)",
                      }}
                      data-testid="rsvp-warmup"
                    >
                      <Flag size={20} style={{ color: "#00E5FF" }} className="shrink-0 glow-blue" />
                      <div className="text-white font-body text-sm sm:text-base leading-snug">
                        <span className="font-display text-[#00E5FF] tracking-wide">
                          Inscrições Abertas
                        </span>{" "}
                        — Garanta sua vaga!
                      </div>
                    </div>
                  )
                )}
              </div>

              {ride.notes ? (
                <div
                  className="mt-5 border-l-2 border-[#00E5FF]/50 pl-4 text-white/80 text-sm whitespace-pre-wrap break-words"
                  data-testid="ride-notes"
                >
                  {ride.notes}
                </div>
              ) : null}

              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-xs w-full sm:w-auto justify-center"
                  data-testid="share-whatsapp-btn"
                >
                  <Share2 size={14} /> Compartilhar no WhatsApp
                </a>
                <button
                  onClick={() => setQrOpen(true)}
                  className="border border-white/15 hover:border-[#00E5FF] px-5 py-2.5 text-xs uppercase tracking-widest text-white inline-flex items-center gap-2 transition-colors w-full sm:w-auto justify-center"
                  data-testid="open-qr-btn"
                >
                  <QrCode size={14} /> QR do Rolê
                </button>
                {ride.show_rsvp_list_public && (
                  <button
                    onClick={() => setVerifyOpen(true)}
                    className="border px-5 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 transition-colors w-full sm:w-auto justify-center hover:bg-[#39FF14]/5"
                    style={{
                      color: "#39FF14",
                      borderColor: "rgba(57,255,20,0.45)",
                    }}
                    data-testid="open-rsvp-list-btn"
                  >
                    <Users size={14} /> Ver Lista de Presença
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <QrCodeDialog
          open={qrOpen}
          onOpenChange={setQrOpen}
          ride={ride}
          url={typeof window !== "undefined" ? window.location.href : ""}
        />

        <RsvpListDialog
          open={listOpen}
          onOpenChange={setListOpen}
          rideId={ride.id}
          rideName={ride.name}
        />

        <PhoneVerifyDialog
          open={verifyOpen}
          onOpenChange={setVerifyOpen}
          rideId={ride.id}
          onVerified={() => setListOpen(true)}
        />

        {/* RSVP form — only for future rides */}
        {!past && (
          <div className="p-6 sm:p-8">
            {success ? (
              <SuccessPanel
                ride={ride}
                rsvp={success.rsvp}
                shareText={shareText}
                onShowList={() => setListOpen(true)}
                onReset={() => {
                  setSuccess(null);
                  setForm({ rider_name: "", phone: "", companion: "", motorcycle_model: "", is_guest: false });
                  setAutoFilled(false);
                }}
              />
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="rsvp-form">
              <div>
                <div className="label-caps mb-2">Confirmar Presença</div>
                <h2 className="font-display text-2xl sm:text-3xl text-white uppercase">
                  Bora nesse rolê?
                </h2>
              </div>

              <FormField label="Telefone (WhatsApp) *" testid="rsvp-phone-field">
                <div className="relative">
                  <Input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    value={form.phone}
                    onChange={(e) => {
                      setForm({ ...form, phone: maskPhoneBR(e.target.value) });
                      setAutoFilled(false);
                    }}
                    onBlur={lookupByPhone}
                    placeholder="(85) 91234-5678"
                    maxLength={16}
                    className="bg-[#0B0D14] border-white/10 focus:border-[#00E5FF] rounded-sm h-11"
                    data-testid="rsvp-phone-input"
                  />
                  {lookingUp && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-widest text-[#00E5FF]">
                      Buscando...
                    </span>
                  )}
                </div>
                {autoFilled && (
                  <p className="text-[11px] text-[#39FF14] mt-1" data-testid="rsvp-autofilled-hint">
                    ✓ Dados preenchidos automaticamente. Ajuste se precisar.
                  </p>
                )}
              </FormField>

              <FormField label="Nome *" testid="rsvp-name-field">
                <Input
                  value={form.rider_name}
                  onChange={(e) => setForm({ ...form, rider_name: e.target.value })}
                  placeholder="Ex: Enzo Ferrari"
                  className="bg-[#0B0D14] border-white/10 focus:border-[#00E5FF] rounded-sm h-11"
                  data-testid="rsvp-name-input"
                />
              </FormField>

              <FormField label="Modelo da Moto *" testid="rsvp-moto-field">
                <Input
                  value={form.motorcycle_model}
                  onChange={(e) =>
                    setForm({ ...form, motorcycle_model: e.target.value })
                  }
                  placeholder="Ex: Kawasaki Ninja H2R"
                  className="bg-[#0B0D14] border-white/10 focus:border-[#00E5FF] rounded-sm h-11"
                  data-testid="rsvp-moto-input"
                />
              </FormField>

              <FormField label="Acompanhante (opcional)" testid="rsvp-companion-field">
                <Input
                  value={form.companion}
                  onChange={(e) => setForm({ ...form, companion: e.target.value })}
                  placeholder="Nome Acompanhante"
                  className="bg-[#0B0D14] border-white/10 focus:border-[#00E5FF] rounded-sm h-11"
                  data-testid="rsvp-companion-input"
                />
              </FormField>

              <label
                className="flex items-center gap-3 select-none cursor-pointer border border-white/10 bg-[#0B0D14] px-4 py-3"
                data-testid="rsvp-guest-toggle"
              >
                <Checkbox
                  checked={form.is_guest}
                  onCheckedChange={(v) => setForm({ ...form, is_guest: !!v })}
                  className="border-white/30 data-[state=checked]:bg-[#00E5FF] data-[state=checked]:text-black"
                  data-testid="rsvp-guest-checkbox"
                />
                <span className="text-white text-sm">
                  Convidado (Não faço parte do grupo Speed Freaks)
                </span>
              </label>

              {rideIsFull && (
                <div
                  className="border px-4 py-3 text-sm mb-1"
                  style={{
                    borderColor: "rgba(245,158,11,0.45)",
                    background: "rgba(245,158,11,0.06)",
                    color: "#F59E0B",
                  }}
                  data-testid="rsvp-full-notice"
                >
                  🔥 Rolê lotado! Ao confirmar, você entra na <b>fila de espera</b> e sobe automaticamente se abrir vaga.
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full h-12 text-sm"
                data-testid="rsvp-submit-btn"
                style={
                  rideIsFull
                    ? {
                        background: "#F59E0B",
                        color: "#000",
                        boxShadow: "0 0 24px rgba(245,158,11,0.4)",
                      }
                    : undefined
                }
              >
                {submitting
                  ? "Enviando..."
                  : rideIsFull
                    ? "Entrar na Fila de Espera"
                    : "Confirmar Presença"}
              </Button>
            </form>
          )}
          </div>
        )}
      </div>
    </div>
  );
}

function PastRideSummary({ ride, rsvpCount, onOpenList }) {
  const hasVideo = ride.video_link && ride.video_link.trim().length > 0;

  return (
    <div className="mt-6 space-y-6" data-testid="past-ride-summary">
      <div className="border border-white/10 p-4 bg-[#0B0D14]" data-testid="past-ride-location">
        <div className="flex items-center gap-2">
          <MapButton address={ride.location} size={18} testid="past-ride-map" />
          <span className="label-caps">Local</span>
        </div>
        <div className="mt-2 text-white font-body break-words">{ride.location}</div>
      </div>

      {ride.notes ? (
        <div
          className="border-l-2 border-white/15 pl-4 text-white/70 text-sm whitespace-pre-wrap break-words"
          data-testid="ride-notes"
        >
          {ride.notes}
        </div>
      ) : null}

      {rsvpCount !== null && rsvpCount > 0 && (
        <div
          className="border border-white/10 bg-gradient-to-r from-[#00E5FF]/5 to-[#39FF14]/5 px-5 py-4 flex items-center gap-3"
          data-testid="past-rider-count"
        >
          <div className="text-white font-body text-sm sm:text-base leading-snug">
            🚀 <span className="font-display text-xl text-[#39FF14] glow-green">{rsvpCount}</span>{" "}
            {rsvpCount === 1 ? "Piloto fez" : "Pilotos fizeram"} história neste rolê!
          </div>
        </div>
      )}

      {ride.show_rsvp_list_public && (
        <button
          onClick={onOpenList}
          className="border w-full sm:w-auto px-5 py-3 text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2 transition-colors hover:bg-[#39FF14]/5"
          style={{ color: "#39FF14", borderColor: "rgba(57,255,20,0.45)" }}
          data-testid="past-open-rsvp-list-btn"
        >
          <Users size={14} /> Ver Lista de Presença
        </button>
      )}

      {hasVideo ? (
        <a
          href={ride.video_link}
          target="_blank"
          rel="noreferrer"
          className="btn-primary inline-flex items-center justify-center gap-3 w-full sm:w-auto px-6 sm:px-8 py-4 text-sm sm:text-base font-black"
          data-testid="past-video-btn"
          style={{ boxShadow: "0 0 32px rgba(0, 229, 255, 0.35)" }}
        >
          <PlayCircle size={22} />
          ASSISTA O VÍDEO DESTE ROLÊ! 🏍️🔥
        </a>
      ) : (
        <div
          className="label-caps text-white/50 text-center py-2"
          data-testid="past-closed-notice"
        >
          Evento encerrado — aguarde o próximo!
        </div>
      )}
    </div>
  );
}

function FormField({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <Label className="label-caps block mb-2">{label}</Label>
      {children}
    </div>
  );
}

function InfoBlock({ icon, label, value, link, testid }) {
  return (
    <div className="border border-white/10 p-4 bg-[#0B0D14] min-w-0" data-testid={testid}>
      <div className="flex items-center gap-2">
        {icon}
        <span className="label-caps">{label}</span>
      </div>
      <div className="mt-2 text-white font-body break-words">{value}</div>
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-xs text-[#00E5FF] hover:underline break-all"
        >
          Abrir link <ExternalLink size={12} />
        </a>
      ) : null}
    </div>
  );
}

function SuccessPanel({ ride, rsvp, shareText, onReset, onShowList }) {
  const code = rsvp?.confirmation_code || "";
  const waitlistPos = rsvp?.waitlist_position;
  const isWaitlist = !!(waitlistPos && waitlistPos >= 1);
  const accent = isWaitlist ? "#F59E0B" : "#39FF14";
  const copyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Código copiado!");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };
  return (
    <div className="text-center py-6" data-testid="rsvp-success">
      <div className="flex justify-center">
        {isWaitlist ? (
          <Users size={56} style={{ color: accent }} className="glow-green" />
        ) : (
          <CheckCircle2 size={56} style={{ color: accent }} className="glow-green" />
        )}
      </div>
      <h3 className="font-display text-3xl sm:text-4xl text-white uppercase mt-4">
        {isWaitlist ? "Você está na fila!" : "Presença confirmada!"}
      </h3>
      {isWaitlist ? (
        <>
          <div
            className="mt-4 mx-auto max-w-sm border-2 border-dashed px-5 py-4"
            style={{
              borderColor: "rgba(245,158,11,0.55)",
              background: "rgba(245,158,11,0.06)",
            }}
            data-testid="rsvp-waitlist-block"
          >
            <div className="label-caps" style={{ color: accent, letterSpacing: "0.3em" }}>
              Sua Posição
            </div>
            <div
              className="mt-2 font-display text-6xl sm:text-7xl tracking-[0.15em] text-white glow-green"
              data-testid="rsvp-waitlist-position"
            >
              #{waitlistPos}
            </div>
            <div className="mt-3 text-white/80 text-xs sm:text-sm leading-snug">
              🔥 O rolê está lotado. Se abrir vaga, você sobe <b>automaticamente</b> na ordem da fila.
            </div>
          </div>
          <p className="mt-4 text-white/70 text-sm max-w-md mx-auto">
            Fique de olho no WhatsApp — a Speed Freaks te chama assim que liberar uma vaga em{" "}
            <span className="font-bold text-[#00E5FF]">{ride.name}</span>.
          </p>
        </>
      ) : (
        <p className="mt-3 text-white/80 max-w-md mx-auto">
          A Speed Freaks agradece sua participação e nos vemos no rolê{" "}
          <span className="font-bold text-[#00E5FF]">{ride.name}</span>.
        </p>
      )}

      {code && (
        <div
          className="mt-6 mx-auto max-w-sm border-2 border-dashed px-5 py-5"
          style={{
            borderColor: "rgba(57,255,20,0.55)",
            background: "rgba(57,255,20,0.05)",
          }}
          data-testid="rsvp-confirmation-block"
        >
          <div className="label-caps" style={{ color: "#39FF14", letterSpacing: "0.3em" }}>
            {isWaitlist ? "Código da Fila" : "Código de Inscrição"}
          </div>
          <button
            type="button"
            onClick={copyCode}
            className="mt-2 font-display text-5xl sm:text-6xl tracking-[0.35em] text-white glow-green hover:text-[#39FF14] transition-colors cursor-pointer"
            title="Toque para copiar"
            data-testid="rsvp-confirmation-code"
          >
            {code}
          </button>
          <div className="mt-3 text-white/70 text-xs sm:text-sm leading-snug">
            🔒 <span className="font-semibold text-white">Salve este código</span> — é a sua prova oficial{isWaitlist ? " de posição na fila" : " de presença no rolê"}. Guarde um print ou copie clicando no código.
          </div>
          <button
            type="button"
            onClick={copyCode}
            className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#39FF14] border border-[#39FF14]/40 hover:border-[#39FF14] px-3 py-1.5 transition-colors"
            data-testid="rsvp-copy-code-btn"
          >
            <Copy size={12} /> Copiar Código
          </button>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3 justify-center">
        <button
          onClick={onShowList}
          className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-xs"
          data-testid="success-view-list-btn"
        >
          <Users size={14} /> Ver Lista de Presença
        </button>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
          target="_blank"
          rel="noreferrer"
          className="border border-white/15 hover:border-[#00E5FF] text-white px-5 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 transition-colors"
          data-testid="success-share-btn"
        >
          <Share2 size={14} /> Compartilhar
        </a>
        <button
          onClick={onReset}
          className="border border-white/15 text-white px-5 py-2.5 text-xs uppercase tracking-widest hover:border-white transition-colors"
          data-testid="rsvp-add-another-btn"
        >
          Confirmar Outro
        </button>
        <Link
          to="/"
          className="border border-white/15 text-white px-5 py-2.5 text-xs uppercase tracking-widest hover:border-white transition-colors"
          data-testid="success-home-btn"
        >
          Voltar
        </Link>
      </div>
    </div>
  );
}
